import { useEffect, useState } from "react";
import { Copy, Loader2, PackageSearch, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const STATUS = { sent: "Product data request sent", queued: "Product data request queued", failed: "Product data request failed" };
const SRC = { manufactured: ["Made", "bg-sky-50 text-sky-700"], resold: ["Bought-in", "bg-emerald-50 text-emerald-700"], needs_mapping: ["Untraceable", "bg-rose-50 text-rose-700"] };

export const SourcingBadge = ({ s, testid }) => SRC[s]
  ? <span data-testid={testid} className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${SRC[s][1]}`}>{SRC[s][0]}</span>
  : <span className="text-slate-400">—</span>;

export const SourcingSelect = ({ value, onChange, testid }) => (
  <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm">
    <option value="">Sourcing: all</option><option value="made">Made</option><option value="bought_in">Bought-in</option><option value="untraceable">Untraceable</option>
  </select>
);

export const ProdReqChip = ({ r, testid }) => r ? (
  <span data-testid={testid} title={r.error || ""} className={`rounded px-1.5 py-0.5 text-[11px] font-semibold ${r.status === "failed" ? "bg-rose-50 text-rose-700" : r.status === "queued" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"}`}>
    {STATUS[r.status] || r.status}{r.at ? ` · ${r.at.slice(0, 10)}` : ""}
  </span>) : null;

const Template = () => {
  const [t, setT] = useState(null);
  useEffect(() => { api.get("/suppliers/product-requests/template").then((r) => setT(r.data)); }, []);
  if (!t) return null;
  const names = { en: "English · EU supplier", fr: "Français · fournisseur UE", en_non_eu: "English · non-EU supplier", fr_non_eu: "Français · fournisseur hors UE" };
  return (
    <div className="grid gap-3 md:grid-cols-2" data-testid="prodreq-template">
      {Object.keys(names).map((k) => (
        <div key={k} className="rounded-lg border border-slate-200 bg-slate-50 p-3">
          <div className="mb-1 flex items-center justify-between"><b className="text-xs uppercase tracking-wider text-slate-500">{names[k]}</b>
            <button className="flex items-center gap-1 text-xs text-[#D52B3B]" data-testid={`prodreq-template-copy-${k}`} onClick={() => navigator.clipboard.writeText(t[k]).then(() => toast.success("Template copied"))}><Copy className="h-3.5 w-3.5" />Copy</button></div>
          <pre className="max-h-40 overflow-auto whitespace-pre-wrap text-xs text-slate-700">{t[k]}</pre>
        </div>))}
    </div>
  );
};

const Sample = ({ s, k }) => s ? (
  <div data-testid={`prodreq-sample-${k}`}><p className="text-xs text-slate-500">{k === "eu" ? "EU supplier" : "Non-EU supplier"} preview: {s.supplier} ({s.to}) · {s.subject}</p>
    <iframe title={`preview ${k}`} srcDoc={s.html} className="mt-1 h-80 w-full rounded border" /></div>) : null;

const List = ({ rows, title, testid, tone }) => rows.length ? (
  <details data-testid={testid}><summary className={`cursor-pointer ${tone}`}>{title} ({rows.length})</summary>
    <ul className="mt-1 max-h-32 overflow-auto text-xs">{rows.map((x) => <li key={x.id}>{x.vendor} · {x.name} ({x.country}) — {x.reason}</li>)}</ul></details>) : null;

export const ProdReqBatch = ({ onDone }) => {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setD(null); api.get("/suppliers/product-requests/preview", { params: { lang } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); } }, [open, lang]);
  const send = async () => {
    setBusy(true);
    try { const r = await api.post("/suppliers/product-requests", {}); toast.success(`${r.data.queued} product data requests queued`); setOpen(false); onDone?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const retry = async () => {
    try { const r = await api.post("/suppliers/product-requests/retry-failed"); toast.success(`${r.data.queued} failed product data requests re-queued`); onDone?.(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <>
      <Button variant="outline" data-testid="prodreq-batch-btn" onClick={() => setOpen(true)}><PackageSearch className="mr-1.5 h-4 w-4" />Request product DDS / plot data</Button>
      <Button variant="outline" data-testid="prodreq-retry-btn" onClick={retry}><RotateCw className="mr-1.5 h-4 w-4" />Retry failed product requests</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto" data-testid="prodreq-dialog">
          <DialogHeader><DialogTitle>Request product DDS / plot data (bought-in products, EUDR applies)</DialogTitle></DialogHeader>
          {!d ? <Loader2 className="h-5 w-5 animate-spin" /> : (
            <div className="space-y-4 text-sm">
              <p data-testid="prodreq-count"><b>{d.count}</b> suppliers with a contact will receive the email ({d.recipients.filter((x) => x.eu).length} EU · {d.recipients.filter((x) => !x.eu).length} non-EU).
                Not included: {d.excluded.intercompany} intercompany, {d.excluded.gb_only} GB sales only — EUDR not applicable, {d.excluded.not_bought_in} without bought-in in-scope products, {d.no_contact.length} without a contact.</p>
              <List rows={d.no_contact} title="No contact — not sent" testid="prodreq-no-contact" tone="text-amber-700" />
              <List rows={d.gb_only} title="GB sales only — EUDR not applicable" testid="prodreq-gb-only" tone="text-slate-500" />
              <div className="flex gap-2">{["en", "fr"].map((k) => <Button key={k} size="sm" variant={lang === k ? "default" : "outline"} data-testid={`prodreq-lang-${k}`} onClick={() => setLang(k)}>{k.toUpperCase()}</Button>)}</div>
              {d.samples.eu || d.samples.non_eu ? <><Sample s={d.samples.eu} k="eu" /><Sample s={d.samples.non_eu} k="non_eu" /></> : <p className="text-slate-500">No recipient to preview. The template text is below.</p>}
              <Template />
            </div>)}
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="prodreq-confirm" disabled={busy || !d || !d.count} onClick={send}>{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Send to {d?.count || 0} suppliers</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export const ProdReqSingle = ({ s, onSent }) => {
  const [busy, setBusy] = useState(false);
  const [st, setSt] = useState(null);
  useEffect(() => { api.get(`/suppliers/${s.id}/product-request`).then((r) => setSt(r.data)).catch(() => {}); }, [s.id]);
  const go = async () => {
    setBusy(true);
    try { const r = await api.post(`/suppliers/${s.id}/product-request`); r.data.ok ? toast.success("Product data request sent") : toast.error(`Email failed: ${r.data.error}`); onSent?.(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (st && !st.applicable) return <span className="text-xs text-slate-500" data-testid="prodreq-single-na">{st.reason}</span>;
  return <Button variant="outline" size="sm" data-testid="prodreq-single-btn" disabled={busy || !st} onClick={go}>{busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <PackageSearch className="mr-1.5 h-4 w-4" />}Request product data{st ? ` (${st.products.length})` : ""}</Button>;
};
